import { notification } from 'antd';
import { addCMSMedia } from 'api/cms-media.api';
import { getSourceId } from 'api/sources.api';
import { DestinationContentSources } from 'constants/destination-content-sources';
import { DestinationContentTypes } from 'constants/destination-content-types';
import { DestinationMapVisibility } from 'constants/destination-map-visibility';
import { DestinationPopularity } from 'constants/destination-popularity';
import { DestinationStatuses } from 'constants/destination-statuses';
import { DESTINATION_MEDIA } from 'constants/media-sizes.constants';
import { useDestinationContentTranslation } from 'hooks/use-destination-content-translation';
import { useFormDirtyState } from 'hooks/use-form-dirty-state';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DestinationContentSchema } from 'typings/destinations/DestinationContentSchema';
import { getPolygonGeometryFromBBox } from 'utils/geojson-utils';
import {
    Destination,
    DestinationContent,
    useDestinationEditor,
} from './DestinationEditorContext';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { DestinationUserSchema } from 'typings/destinations/DestinationUserSchema';

const defaultTagline: DestinationContent = {
    content_type: DestinationContentTypes.TAGLINE,
    body: '',
    order: 0,
    source: DestinationContentSources.MANUAL,
};

const defaultDescription: DestinationContent = {
    content_type: DestinationContentTypes.DESCRIPTION,
    body: '',
    order: 0,
    source: DestinationContentSources.MANUAL,
};

export type FieldErrors = {
    tagline?: string;
    location?: string;
    name?: string;
};

export type FormValues = {
    name: string | null;
    boundary_area: string | null;
    cover_image: CMSMedia | null;
    location: PSLocation | null;
    taglineContent: string;
    descriptionContent: string;
    status: number;
    map_visibility: number;
    popularity: number;
    main_editor_id: number | null;
    main_editor: DestinationUserSchema | null;
};

export const popularityOptions = [
    {
        label: '1 - Rarely explored',
        value: DestinationPopularity.RARELY_EXPLORED,
    },
    {
        label: '2 - Occasionally explored',
        value: DestinationPopularity.OCCASIONALLY_EXPLORED,
    },
    {
        label: '3 - Regularly explored',
        value: DestinationPopularity.REGULARLY_EXPLORED,
    },
    {
        label: '4 - Frequently explored',
        value: DestinationPopularity.FREQUENTLY_EXPLORED,
    },
    {
        label: '5 - Highly explored',
        value: DestinationPopularity.HIGHLY_EXPLORED,
    },
    {
        label: '6 - Intensely explored',
        value: DestinationPopularity.INTENSIVELY_EXPLORED,
    },
];

export const mapVisibilityOptions = [
    { label: '0 - Always hidden', value: DestinationMapVisibility.HIDDEN },
    { label: '1', value: DestinationMapVisibility.HIGHEST_ZOOM },
    { label: '2', value: DestinationMapVisibility.HIGH_ZOOM },
    { label: '3', value: DestinationMapVisibility.MEDIUM_ZOOM },
    { label: '4', value: DestinationMapVisibility.LOW_ZOOM },
    {
        label: '5 - Always visible',
        value: DestinationMapVisibility.ALL_ZOOM_LEVELS,
    },
];

export function useDestinationBasicInfoForm() {
    const {
        destination,
        location,
        updateDestinationField,
        updateLocation,
        updateDestination,
        saveDestination,
        saveBulkContent,
        isEditing,
        isCountry,
        isContentCurated,
        locale,
        hasContent,
    } = useDestinationEditor();

    const [isSaving, setIsSaving] = useState(false);
    const [isTogglingContentCurated, setIsTogglingContentCurated] = useState(
        false,
    );
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [imageToPurchase, setImageToPurchase] = useState<CMSMedia | null>(
        null,
    );
    const [
        isAddingStockPhotoWarningShown,
        setIsAddingStockPhotoWarningShown,
    ] = useState(false);

    const tagline = useMemo(
        () =>
            destination?.contents?.find(
                (c) => c.content_type === DestinationContentTypes.TAGLINE,
            ) ?? defaultTagline,
        [destination],
    );

    const {
        translatedContent: taglineValue,
        isTranslation,
        handleUpdateTranslationField: handleUpdateTaglineTranslationField,
    } = useDestinationContentTranslation({ content: tagline, locale });

    const description = useMemo(
        () =>
            destination?.contents?.find(
                (c) => c.content_type === DestinationContentTypes.DESCRIPTION,
            ) ?? defaultDescription,
        [destination],
    );

    const {
        translatedContent: descriptionValue,
        handleUpdateTranslationField: handleUpdateDescriptionTranslationField,
    } = useDestinationContentTranslation({ content: description, locale });

    const [taglineContent, setTaglineContent] = useState(tagline?.body ?? '');
    const [descriptionContent, setDescriptionContent] = useState(
        description?.body ?? '',
    );

    const formValues: FormValues = useMemo(
        () => ({
            name: destination?.name ?? '',
            boundary_area: destination?.boundary_area ?? null,
            cover_image: destination?.cover_image ?? null,
            location,
            taglineContent,
            descriptionContent,
            status: destination?.status ?? DestinationStatuses.DRAFT,
            map_visibility:
                destination?.map_visibility ??
                DestinationMapVisibility.HIGHEST_ZOOM,
            popularity:
                destination?.popularity ??
                DestinationPopularity.RARELY_EXPLORED,
            main_editor_id: destination?.main_editor_id ?? null,
            main_editor: destination?.main_editor ?? null,
        }),
        [destination, location, taglineContent, descriptionContent],
    );

    const { isDirty, updateOriginalValues, originalValues } = useFormDirtyState(
        {
            values: formValues,
        },
    );

    const validateFields = useCallback(() => {
        const errors: FieldErrors = {};
        if (!isCountry && !location) {
            errors.location = 'Location is required';
        }
        if (!destination?.name) {
            errors.name = isCountry
                ? 'Country name is required'
                : 'Destination name is required';
        }
        setFieldErrors(errors);
        return Object.values(errors).every((value) => !value);
    }, [isCountry, location, destination?.name]);

    const handleUpdateField = useCallback(
        <T extends keyof Destination>(field: T, value: Destination[T]) => {
            updateDestinationField(field, value);
        },
        [updateDestinationField],
    );

    const mapContents = useCallback(
        (
            content: DestinationContentSchema,
            body: string,
            type:
                | typeof DestinationContentTypes.TAGLINE
                | typeof DestinationContentTypes.DESCRIPTION,
        ) => {
            if (content.content_type !== type) return content;
            if (!isTranslation) {
                return {
                    ...content,
                    source: DestinationContentSources.MANUAL,
                    body,
                };
            }
            return {
                ...content,
                source: DestinationContentSources.MANUAL,
                translations:
                    type === DestinationContentTypes.TAGLINE
                        ? handleUpdateTaglineTranslationField({
                              destination_content_id: content.id ?? 0,
                              body,
                          })
                        : handleUpdateDescriptionTranslationField({
                              destination_content_id: content.id ?? 0,
                              body,
                          }),
            };
        },
        [
            isTranslation,
            handleUpdateTaglineTranslationField,
            handleUpdateDescriptionTranslationField,
        ],
    );

    const handleUpdateTaglineOrDescription = useCallback(
        (
            body: string,
            type:
                | typeof DestinationContentTypes.TAGLINE
                | typeof DestinationContentTypes.DESCRIPTION,
        ) => {
            if (
                type === DestinationContentTypes.TAGLINE &&
                !body.trim().length
            ) {
                setFieldErrors((prev) => ({ ...prev, tagline: undefined }));
            }
            if (!destination) return;
            handleUpdateField(
                'contents',
                destination.contents?.map((content) =>
                    mapContents(content, body, type),
                ) ?? [],
            );
        },
        [destination, handleUpdateField, mapContents],
    );

    const handleUpdateCoverImage = useCallback(
        async (media: CMSMedia | null) => {
            const shutterstockSourceId = await getSourceId('shutterstock');
            if (media?.source_id === shutterstockSourceId) {
                setImageToPurchase(media);
            }
            handleUpdateField('cover_image', media ?? null);
        },
        [handleUpdateField],
    );

    const handleSaveCoverImage = useCallback(async () => {
        if (!destination) return null;
        const addedMedia =
            destination.cover_image && destination.cover_image.id === undefined
                ? await addCMSMedia(destination.cover_image, DESTINATION_MEDIA)
                : destination.cover_image;
        return addedMedia;
    }, [destination]);

    const handleSaveDestination = useCallback(
        async (purchasedPhoto?: CMSMedia) => {
            if (!destination) return;
            setIsSaving(true);
            try {
                const contentsToSave: DestinationContentSchema[] = [];
                if (taglineContent !== originalValues?.taglineContent) {
                    contentsToSave.push(
                        mapContents(
                            tagline as DestinationContentSchema,
                            taglineContent,
                            DestinationContentTypes.TAGLINE,
                        ),
                    );
                }
                if (descriptionContent !== originalValues?.descriptionContent) {
                    contentsToSave.push(
                        mapContents(
                            description as DestinationContentSchema,
                            descriptionContent,
                            DestinationContentTypes.DESCRIPTION,
                        ),
                    );
                }
                const addedMedia =
                    purchasedPhoto ?? (await handleSaveCoverImage());
                const destinationToSave = {
                    ...destination,
                    cover_image_id: addedMedia ? addedMedia.id : null,
                    cover_image: addedMedia,
                };
                await saveDestination({
                    destination: destinationToSave,
                    contents: contentsToSave,
                });
                updateOriginalValues();
            } catch (error) {
                console.error(error);
                notification.error({
                    message: `Error saving destination: ${error}`,
                });
            } finally {
                setIsSaving(false);
            }
        },
        [
            destination,
            tagline,
            description,
            taglineContent,
            descriptionContent,
            originalValues,
            mapContents,
            handleSaveCoverImage,
            saveDestination,
            updateOriginalValues,
        ],
    );

    const handleSave = useCallback(async () => {
        if (!validateFields()) {
            notification.error({
                message: 'Please fix the errors before saving',
            });
            return;
        }
        if (imageToPurchase) {
            setIsAddingStockPhotoWarningShown(true);
        } else {
            await handleSaveDestination();
        }
    }, [validateFields, imageToPurchase, handleSaveDestination]);

    const handleAddPurchasedPhoto = useCallback(
        async (purchasedPhotos: CMSMedia[]) => {
            setIsAddingStockPhotoWarningShown(false);
            setImageToPurchase(null);
            handleUpdateField('cover_image', purchasedPhotos[0]);
            await handleSaveDestination(purchasedPhotos[0]);
        },
        [handleUpdateField, handleSaveDestination],
    );

    const handleToggleContentCurated = useCallback(
        async (e: CheckboxChangeEvent) => {
            const isCurated = e.target.checked;
            if (!destination?.id) return;
            setIsTogglingContentCurated(true);
            try {
                const updatedContents = await saveBulkContent({
                    destinationId: destination.id,
                    contents: (destination.contents ?? []).map((content) => ({
                        ...content,
                        id: content.id > 0 ? content.id : undefined,
                        source: isCurated
                            ? DestinationContentSources.MANUAL
                            : DestinationContentSources.AI,
                        translations: content.translations,
                    })),
                });
                updateDestination({
                    ...destination,
                    contents: updatedContents,
                });
                notification.success({
                    message: 'Content curated status updated successfully',
                    key: 'content-curated-status-updated',
                });
            } finally {
                setIsTogglingContentCurated(false);
            }
        },
        [destination, saveBulkContent, updateDestination],
    );

    const handleUpdateEditor = useCallback(
        (newEditorId: Id, newEditor: CMSUser) => {
            const destinationUser: DestinationUserSchema = {
                id: Number(newEditorId),
                first_name: newEditor.first_name,
                last_name: newEditor.last_name,
                profile_image: newEditor.google_profile_image,
            };
            handleUpdateField('main_editor_id', destinationUser.id);
            handleUpdateField('main_editor', destinationUser);
        },
        [handleUpdateField],
    );

    const handleUpdateCountryName = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            handleUpdateField('name', value);
            handleUpdateField('country', value);
            if (value) {
                setFieldErrors((prev) => ({ ...prev, name: undefined }));
            }
        },
        [handleUpdateField],
    );

    const handleLocationChange = useCallback(
        (newLocation: PSLocationWithBB) => {
            if (newLocation) {
                setFieldErrors((prev) => ({ ...prev, location: undefined }));
            }
            updateLocation(newLocation);
            handleUpdateField('name', newLocation.name);
            if (isCountry) {
                handleUpdateField('locality', null);
            }
            handleUpdateField(
                'boundary_area',
                JSON.stringify(
                    getPolygonGeometryFromBBox(newLocation.boundingbox),
                ),
            );
        },
        [handleUpdateField, updateLocation, isCountry],
    );

    useEffect(() => {
        setTaglineContent(taglineValue?.body ?? '');
    }, [taglineValue?.body]);

    useEffect(() => {
        setDescriptionContent(descriptionValue?.body ?? '');
    }, [descriptionValue?.body]);

    return {
        destination,
        location,
        isCountry,
        isEditing,
        isContentCurated,
        hasContent,
        fieldErrors,
        taglineContent,
        setTaglineContent,
        descriptionContent,
        setDescriptionContent,
        isDirty,
        isSaving,
        isTogglingContentCurated,
        imageToPurchase,
        isAddingStockPhotoWarningShown,
        setIsAddingStockPhotoWarningShown,
        validateFields,
        handleUpdateCoverImage,
        handleSave,
        handleAddPurchasedPhoto,
        handleUpdateField,
        handleUpdateTaglineOrDescription,
        handleToggleContentCurated,
        handleUpdateEditor,
        handleUpdateCountryName,
        handleLocationChange,
    };
}
